import { Button } from "@coval/ui"
import {
  ArrowLeft,
  CalendarClock,
  Check,
  CircleAlert,
  History,
  RotateCcw,
  X,
} from "lucide-react"

import type { Cadence } from "../../utils/reportTypes"
import { reportClass } from "./ReportStyles"

/** The builder's error, notice, and historical-revision banners. */
export function ReportMessages({
  error,
  notice,
  previewRevision,
  locked,
  onDismissError,
  onDismissNotice,
  onLatest,
  onRestore,
  unscheduled,
}: {
  error: string
  notice: string
  /** The older revision on screen, or null on the latest one. */
  previewRevision: number | null
  locked: boolean
  onDismissError: () => void
  onDismissNotice: () => void
  onLatest: () => void
  onRestore: () => void
  /**
   * Set while the open notebook is saved but its schedule is not. It outlives
   * the error banner, so dismissing the error never strands the retry.
   */
  unscheduled?: {
    cadence: Cadence
    onRetry: () => void
    onDiscard: () => void
  } | null
}) {
  return (
    <>
      {error && (
        <div className={reportClass("message error-message")} role="alert">
          <CircleAlert size={16} />
          <span>{error}</span>
          <Button
            variant="ghost"
            icon
            tooltip="Dismiss error"
            onClick={onDismissError}
          >
            <X />
          </Button>
        </div>
      )}
      {unscheduled && (
        <div className={reportClass("message preview-message")} role="status">
          <CalendarClock size={15} />
          <span>
            This notebook is saved, but its {unscheduled.cadence} schedule is
            not.
          </span>
          <Button
            variant="secondary"
            disabled={locked}
            onClick={unscheduled.onRetry}
          >
            Retry schedule
          </Button>
          <Button
            variant="ghost"
            icon
            tooltip="Keep it unscheduled"
            disabled={locked}
            onClick={unscheduled.onDiscard}
          >
            <X />
          </Button>
        </div>
      )}
      {notice && (
        <div className={reportClass("message notice-message")} role="status">
          <Check size={15} />
          <span>{notice}</span>
          <Button
            variant="ghost"
            icon
            tooltip="Dismiss notice"
            onClick={onDismissNotice}
          >
            <X />
          </Button>
        </div>
      )}
      {previewRevision !== null && (
        <div className={reportClass("message preview-message")}>
          <History size={15} />
          <span>
            Viewing revision {previewRevision}. Your latest version is
            unchanged.
          </span>
          <Button variant="secondary" disabled={locked} onClick={onLatest}>
            <ArrowLeft />
            Latest
          </Button>
          <Button disabled={locked} onClick={onRestore}>
            <RotateCcw />
            Restore
          </Button>
        </div>
      )}
    </>
  )
}
